import Image from "next/image";
import type { GetServerSidePropsContext } from "next/types";
import { SWRConfig } from "swr";

import Bugsnag from "@bugsnag/js";

import ToolMnaTransactionsComparable from "components/ToolMnaTransactions";
import { mnaPeersComparisonUrl } from "components/ToolMnaTransactions/PeersTradeComparison";
import ToolPageLayout from "components/ToolPageLayout";
import type { NextPageWithLayout } from "pages/_app";
import Error from "pages/_error";
import {
  authenticatedAccountUrl,
  sessionFromContextCookie,
} from "hooks/useAuthentication";
import { baseFetcher } from "utils/baseFetcher";

const comparablesIcon = require("assets/comparables.svg");

const ToolMergingAcquisitionsTransactionsComparablePage: NextPageWithLayout =
  ({}: any) => {
    return <ToolMnaTransactionsComparable />;
  };

export async function getServerSideProps(context: GetServerSidePropsContext) {
  try {
    const session = sessionFromContextCookie(context);
    const accountId = session?.authenticated?.account_id;
    const accountUrl = authenticatedAccountUrl(accountId);
    const account = await baseFetcher(accountUrl, context);
    const currentEntityId = context.query["entity-id"];

    if (currentEntityId) {
      const currentEntity = await baseFetcher(
        mnaPeersComparisonUrl(currentEntityId),
        context
      );

      if (!currentEntity || currentEntity?.status === "unauthorized") {
        return {
          notFound: true,
        };
      }
    }

    try {
      await baseFetcher(
        `/api/v2/tools/comparables?public_allowed=true`,
        context
      );
    } catch {}

    return {
      props: {
        fallback: {
          [accountUrl]: account,
        },
      },
    };
  } catch (error: any) {
    console.error(
      "[slug].tsx ~ getServerSideProps Peers Comparison Page ~ error",
      error
    );
    Bugsnag.notify(error);
    return {
      props: {
        fallback: {},
      },
    };
  }
}

ToolMergingAcquisitionsTransactionsComparablePage.getLayout = (
  page,
  { pageProps }
) => {
  const { fallback, errorCode } = pageProps;

  if (errorCode) return <Error statusCode={errorCode} />;

  return (
    <SWRConfig value={{ fallback }}>
      <ToolPageLayout
        breadcrumb={[
          {
            title: "Comparables",
            href: `/tools/comparables`,
            icon: (
              <Image
                src={comparablesIcon}
                alt="Comparables"
                width={20}
                height={20}
              />
            ),
          },
        ]}
        title="Comparables"
        description="Publicly Traded Comparables and Precedent Transactions"
        shareable
      >
        {page}
      </ToolPageLayout>
    </SWRConfig>
  );
};

export default ToolMergingAcquisitionsTransactionsComparablePage;
